'use client';

import Link from 'next/link';

import { useTranslations } from '@power-core/toolkit';
import type { FunctionComponent } from 'react';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { Button } from '@/ui-components/button/button';
import { Checkbox } from '@/ui-components/checkbox/checkbox';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Modal } from '@/ui-components/modal/modal';
import { Paragraph } from '@/ui-components/paragraph/paragraph';

import { useCookieSettings } from './use-cookie-settings';

export const CookieSettingsModal: FunctionComponent = () => {
	const t = useTranslations();
	const { appConfig } = useAppContext();
	const {
		setAnalyticsConcent,
		setPreferencesConcent,
		handleAllButton,
		handleSelectedButton,
		analyticsConsent,
		preferencesConcent,
		noneSelected,
		// locale
	} = useCookieSettings();
	const { isDarkTheme } = useTheme();
	//TODO Commented out for now, as the privacy notice URL is a static link.
	// const privacyNoticeUrl = appConfig?.privacyPolicyUrl ? `${appConfig?.privacyPolicyUrl}/${locale}` : '#';

	return (
		<Modal name='CookieSettings' title={{ text: t('T_COOKIE_SETTINGS_TITLE'), variant: 'h5' }} dismissible closeButton withLine>
			<Flexbox direction='col' className='max-w-[600px]'>
				<Paragraph size={14} className='text-n500_light_text'>
					{t('T_COOKIE_SETTINGS_DESCRIPTION')}{' '}
					<Link href={appConfig?.privacyPolicyUrl ? appConfig?.privacyPolicyUrl : '#'} target='_blank' className='text-sm text-accent pb-4'>
						{t('T_PRIVACY_NOTICE')}.
					</Link>
				</Paragraph>
				<Paragraph className='pt-4'>{t('T_COOKIE_OPTIONS')}</Paragraph>
				<Checkbox
					color={noneSelected || isDarkTheme ? 'primary' : 'secondary'}
					sizing='small'
					label={t('T_COOKIE_ANALYTICS')}
					checked={analyticsConsent}
					onChange={(e) => setAnalyticsConcent(e.target.checked)}
				/>
				<Paragraph size={14} className='pl-6 text-n500_light_text'>
					{t('T_COOKIE_ANALYTICS_DESCRIPTION')}
				</Paragraph>
				<Checkbox
					color={noneSelected || isDarkTheme ? 'primary' : 'secondary'}
					sizing='small'
					label={t('T_COOKIE_PREFERENCES')}
					checked={preferencesConcent}
					onChange={(e) => setPreferencesConcent(e.target.checked)}
				/>
				<Paragraph size={14} className='pl-6 text-n500_light_text'>
					{t('T_COOKIE_PREFERENCES_DESCRIPTION')}
				</Paragraph>
				<Flexbox justify='end' className='gap-3 mt-11 [&>*]:w-36'>
					<Button variant='ghost-secondary' onClick={handleSelectedButton}>
						{t('T_ACCEPT_SELECTED')}
					</Button>

					<Button variant='ghost-secondary' onClick={() => handleAllButton(false)}>
						{t('T_DECLINE_ALL')}
					</Button>
					<Button variant='ghost-secondary' onClick={() => handleAllButton(true)}>
						{t('T_ACCEPT_ALL')}
					</Button>
				</Flexbox>
			</Flexbox>
		</Modal>
	);
};
